import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Instagram, Mail, ChevronDown, ArrowUpRight, ExternalLink } from "lucide-react";
import { usePrograms, useSettings, resolveUrl } from "../lib/useCMS";
import { slugifyTitle } from "../lib/slug";

const ABOUT_LINKS = [
  { label: "About", to: "/about" },
  { label: "Philosophy", to: "/philosophy" },
  { label: "Coach Mike", to: "/coach" },
];

const NAV_LINKS = [
  { label: "Programs", to: "/programs", dropdown: "programs" },
  { label: "What is BDH", to: "/about", dropdown: "about" },
  { label: "The Book", to: "/book" },
  { label: "Contact", to: "/contact" },
  {
    label: "Performance Partner | 1st Phorm",
    shortLabel: "1st Phorm",
    to: "https://1stphorm.com/?a_aid=qi5twfdib5lg9",
    external: true,
  },
];

export const Header = () => {
  const settings = useSettings({
    logo_url: "https://customer-assets.emergentagent.com/job_hockey-landing-v2/artifacts/7dc9f10s_Built%20different%20primary%20_PNG.avif",
    brand_name: "Built Different Hockey",
    square_booking_url: "https://book.squareup.com/classes/krpp68j9ccjtzm/location/N7TC3KA55X2D8/classes",
    instagram_url: "https://www.instagram.com/builtdifferenthockey/",
    contact_email: "mike@builtdifferenthockey.com",
  });
  const programs = usePrograms([]);
  const location = useLocation();

  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  // Which desktop dropdown is open (null | "programs" | "about")
  const [openDropdown, setOpenDropdown] = useState(null);
  // Which mobile accordion is open (null | "programs" | "about")
  const [mobileAccordion, setMobileAccordion] = useState(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setOpen(false);
    setMobileAccordion(null);
    setOpenDropdown(null);
  }, [location.pathname]);

  // Lock body scroll while mobile menu is open
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [open]);

  const isActive = (to) => location.pathname === to || (to !== "/" && location.pathname.startsWith(to + "/"));

  // Items for each desktop dropdown
  const dropdownItems = (key) => {
    if (key === "programs") {
      return programs.map((pr) => ({
        key: pr.id || slugifyTitle(pr.title || ""),
        slug: pr.slug || slugifyTitle(pr.title || ""),
        label: pr.title,
        to: `/programs/${pr.slug || slugifyTitle(pr.title || "")}`,
        leadLabel: "All Programs",
        leadTo: "/programs",
      }));
    }
    if (key === "about") {
      return ABOUT_LINKS.map((l) => ({ key: l.to, slug: l.to.slice(1), label: l.label, to: l.to }));
    }
    return [];
  };

  const renderDropdownPanel = (l) => {
    if (l.dropdown === "programs") {
      return (
        <div className="bg-[#0c0c0c] border border-white/10 shadow-2xl">
          <Link
            to="/programs"
            className="block px-5 py-3 text-[10px] tracking-[0.3em] uppercase text-brand-light font-bold border-b border-white/10 hover:bg-white/5"
            data-testid="dropdown-all-programs"
          >
            All Programs
          </Link>
          {programs.length === 0 && (
            <div className="px-5 py-3 text-xs text-zinc-500">Loading…</div>
          )}
          {programs.map((pr) => {
            const slug = pr.slug || slugifyTitle(pr.title || "");
            return (
              <Link
                key={pr.id || slug}
                to={`/programs/${slug}`}
                data-testid={`dropdown-program-${slug}`}
                className="block px-5 py-3.5 text-sm font-display uppercase tracking-tight text-zinc-200 hover:text-white hover:bg-brand transition-colors border-b border-white/5 last:border-0"
              >
                {pr.title}
              </Link>
            );
          })}
        </div>
      );
    }
    return (
      <div className="bg-[#0c0c0c] border border-white/10 shadow-2xl" data-testid="about-dropdown">
        {ABOUT_LINKS.map((item) => (
          <Link
            key={item.to}
            to={item.to}
            data-testid={`dropdown-about-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
            className="block px-5 py-3.5 text-sm font-display uppercase tracking-tight text-zinc-200 hover:text-white hover:bg-brand transition-colors border-b border-white/5 last:border-0"
          >
            {item.label}
          </Link>
        ))}
      </div>
    );
  };

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
          scrolled || open ? "bg-black/85 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
        }`}
      >
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16">
          <div className="flex items-center justify-between h-24 lg:h-28 xl:h-32 gap-3">
            <Link to="/" data-testid="header-logo-link" className="flex items-center group shrink-0" onClick={() => window.scrollTo(0, 0)}>
              <img
                src={resolveUrl(settings.logo_url)}
                alt={settings.brand_name}
                fetchPriority="high"
                decoding="async"
                className="h-14 lg:h-[72px] xl:h-[88px] 2xl:h-[100px] w-auto object-contain transition-transform group-hover:scale-105"
              />
            </Link>

            <nav className="hidden lg:flex items-center lg:gap-3.5 xl:gap-6 2xl:gap-8 min-w-0">
              {NAV_LINKS.map((l) => {
                if (l.dropdown) {
                  const isOpen = openDropdown === l.dropdown;
                  return (
                    <div
                      key={l.dropdown}
                      className="relative"
                      onMouseEnter={() => setOpenDropdown(l.dropdown)}
                      onMouseLeave={() => setOpenDropdown((cur) => (cur === l.dropdown ? null : cur))}
                    >
                      <Link
                        to={l.to}
                        data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                        className={`text-[11px] xl:text-xs tracking-[0.18em] xl:tracking-[0.2em] uppercase font-semibold transition-colors flex items-center gap-1.5 py-2 whitespace-nowrap ${
                          isActive(l.to) ? "text-brand-light" : "text-zinc-200 hover:text-brand-light"
                        }`}
                      >
                        {l.label}
                        <ChevronDown className={`h-3 w-3 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                      </Link>
                      <AnimatePresence>
                        {isOpen && (
                          <motion.div
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 8 }}
                            transition={{ duration: 0.18 }}
                            className="absolute left-1/2 -translate-x-1/2 top-full pt-3 w-[280px]"
                            data-testid={`${l.dropdown}-dropdown`}
                          >
                            {renderDropdownPanel(l)}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                }
                if (l.external) {
                  return (
                    <a
                      key={l.to}
                      href={l.to}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                      className="text-[11px] xl:text-xs tracking-[0.18em] xl:tracking-[0.2em] uppercase font-semibold transition-colors py-2 flex items-center gap-1.5 whitespace-nowrap text-zinc-200 hover:text-brand-light"
                    >
                      <span className="xl:hidden">{l.shortLabel || l.label}</span>
                      <span className="hidden xl:inline">{l.label}</span>
                      <ExternalLink className="h-3 w-3 opacity-70" />
                    </a>
                  );
                }
                return (
                  <Link
                    key={l.to}
                    to={l.to}
                    data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className={`text-[11px] xl:text-xs tracking-[0.18em] xl:tracking-[0.2em] uppercase font-semibold transition-colors py-2 whitespace-nowrap ${
                      isActive(l.to) ? "text-brand-light" : "text-zinc-200 hover:text-brand-light"
                    }`}
                  >
                    {l.label}
                  </Link>
                );
              })}
            </nav>

            <div className="hidden lg:flex items-center gap-2 xl:gap-3 shrink-0">
              <a
                href={settings.instagram_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="header-instagram-link"
                className="hidden xl:grid h-10 w-10 place-items-center border border-white/15 hover:border-brand-light hover:text-brand-light transition-colors"
                aria-label="Instagram"
              >
                <Instagram className="h-4 w-4" />
              </a>
              <a
                href={settings.square_booking_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="header-book-now-button"
                className="btn-primary !py-2.5 !px-3.5 xl:!py-3 xl:!px-5 !text-[11px] xl:!text-sm whitespace-nowrap"
              >
                <span className="hidden xl:inline">Book a Clinic</span>
                <span className="xl:hidden">Book</span>
              </a>
            </div>

            <button
              onClick={() => setOpen((o) => !o)}
              className="lg:hidden h-12 w-12 grid place-items-center border border-white/15 hover:border-brand-light transition-colors"
              data-testid="mobile-menu-toggle"
              aria-label="Toggle menu"
              aria-expanded={open}
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </header>

      {/* Full-screen mobile / tablet menu */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="lg:hidden fixed inset-0 z-40 bg-[#0a0a0a]"
            data-testid="mobile-menu"
          >
            <div className="h-full w-full overflow-y-auto pt-24 pb-12">
              <div className="px-6 md:px-10 max-w-[800px] mx-auto">
                <nav className="flex flex-col">
                  {NAV_LINKS.map((l, idx) => {
                    if (l.dropdown) {
                      const isOpenMobile = mobileAccordion === l.dropdown;
                      const items = dropdownItems(l.dropdown);
                      const hasLead = l.dropdown === "programs";
                      return (
                        <div key={l.dropdown} className="border-b border-white/10">
                          <button
                            onClick={() => setMobileAccordion((cur) => (cur === l.dropdown ? null : l.dropdown))}
                            className="w-full flex items-center justify-between py-5 text-left"
                            data-testid={`mobile-${l.dropdown}-toggle`}
                            aria-expanded={isOpenMobile}
                          >
                            <motion.span
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: 0.1 + idx * 0.05 }}
                              className="font-display text-4xl md:text-5xl uppercase leading-[0.95] tracking-tight"
                            >
                              {l.label}
                            </motion.span>
                            <ChevronDown className={`h-6 w-6 text-brand-light transition-transform shrink-0 ${isOpenMobile ? "rotate-180" : ""}`} />
                          </button>
                          <AnimatePresence>
                            {isOpenMobile && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: "auto", opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.25 }}
                                className="overflow-hidden"
                              >
                                <div className="pb-5 pl-2 flex flex-col gap-3">
                                  {hasLead && (
                                    <Link
                                      to="/programs"
                                      onClick={() => setOpen(false)}
                                      data-testid="mobile-dropdown-all-programs"
                                      className="text-[11px] tracking-[0.3em] uppercase text-brand-light font-bold py-2"
                                    >
                                      → All Programs
                                    </Link>
                                  )}
                                  {items.map((it) => (
                                    <Link
                                      key={it.key}
                                      to={it.to}
                                      onClick={() => setOpen(false)}
                                      data-testid={`mobile-dropdown-${l.dropdown}-${it.slug}`}
                                      className="font-display text-xl uppercase text-zinc-300 hover:text-brand-light transition-colors py-1.5"
                                    >
                                      {it.label}
                                    </Link>
                                  ))}
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      );
                    }
                    if (l.external) {
                      return (
                        <a
                          key={l.to}
                          href={l.to}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => setOpen(false)}
                          data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                          className="border-b border-white/10 py-5 flex items-start justify-between gap-3"
                        >
                          <motion.span
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.1 + idx * 0.05 }}
                            className="font-display text-3xl sm:text-4xl md:text-5xl uppercase leading-[0.95] tracking-tight text-white"
                          >
                            {l.label}
                          </motion.span>
                          <ExternalLink className="h-6 w-6 text-brand-light mt-2 shrink-0" />
                        </a>
                      );
                    }
                    return (
                      <Link
                        key={l.to}
                        to={l.to}
                        onClick={() => setOpen(false)}
                        data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                        className="border-b border-white/10 py-5"
                      >
                        <motion.span
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.1 + idx * 0.05 }}
                          className="font-display text-4xl md:text-5xl uppercase leading-[0.95] tracking-tight text-white hover:text-brand-light transition-colors block"
                        >
                          {l.label}
                        </motion.span>
                      </Link>
                    );
                  })}
                </nav>

                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.45 }}
                  className="mt-10"
                >
                  <a
                    href={settings.square_booking_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="mobile-book-now-button"
                    className="w-full btn-primary !py-5 !text-base justify-center"
                  >
                    Book a Clinic <ArrowUpRight className="h-5 w-5" />
                  </a>

                  <div className="flex items-center gap-3 mt-8">
                    <a
                      href={settings.instagram_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="mobile-instagram-link"
                      className="h-11 w-11 grid place-items-center border border-white/15 hover:border-brand-light hover:text-brand-light transition-colors"
                      aria-label="Instagram"
                    >
                      <Instagram className="h-4 w-4" />
                    </a>
                    <a
                      href={`mailto:${settings.contact_email}`}
                      data-testid="mobile-email-link"
                      className="h-11 w-11 grid place-items-center border border-white/15 hover:border-brand-light hover:text-brand-light transition-colors"
                      aria-label="Email"
                    >
                      <Mail className="h-4 w-4" />
                    </a>
                  </div>
                </motion.div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
